import { Loading } from "components/panels";
import React, { useContext } from "react";
import { useCustomQuery } from "utilities/hookutils";
import moment from "moment";
import WeekRow from "./WeekRow";
import PlaceholderCalendar from "./PlaceholderCalendar";
import { FilterContext } from "components/filters/FilterContext";
import { getCacheFriendlyInstruction } from "components/filters/Filters/InstructionHelpers";
import { PhosphorCaretLeftButton, PhosphorCaretRightButton } from "components/Atoms/PhosphorIconButton";
import { HorizontalKeyboardNavigationGroup } from "keyboardNavigation/KeyboardNavigationPresets";
import { firstDayOfWeek } from "utilities";

interface Props {
  instruction: server.dto.PaginatedShowInternalInstruction
  isDoingFirstLoad: boolean
}

const Calendar = (props: Props) => {
  const [highlightingMonthNr, setHighlightingMonthNr] = React.useState<number>(undefined);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const { setValue } = useContext(FilterContext);

  const onWeekClicked = (week: server.dto.MiniCalendarWeek) => {
    const startDate = moment.utc(week.days[0].date);
    const endDate = startDate.clone().add(1, "week");

    setValue("period", {
      period: "perWeek",
      selectionStart: startDate,
      selectionEnd: endDate,
      target: "sendDate"
    });
  };

  const onDayClicked = (day: server.dto.MiniCalendarDay) => {
    const startDate = moment.utc(day.date);
    const endDate = startDate.clone().endOf("day");

    setValue("period", {
      period: "perDay",
      selectionStart: startDate,
      selectionEnd: endDate,
      target: "sendDate"
    });
  };

  const onMonthSelected = (month: number, year: number) => {
    const startDate = moment.utc({ year, month, day: 1 });
    const endDate = startDate.clone().add(1, "day");

    setValue("period", {
      period: "perMonth",
      selectionStart: startDate,
      selectionEnd: endDate,
      target: "sendDate"
    });
  };

  const getStartDate = () => {
    if (props.instruction.period == null || props.instruction.period.period === "unknown") { return moment.utc(); }

    switch (props.instruction.period.period) {
      case "currentMonth":
        return moment.utc().startOf("month");
      case "currentQuarter": {
        const quarter = Math.floor(moment.utc().month() / 3);
        return moment.utc().month(quarter * 3).startOf("month");
      }
      case "currentWeek":
        return moment.utc().startOf("week");
      case "currentYear":
        return moment.utc().startOf("year");
      case "fromToday":
        return moment.utc().startOf("day");

      default: {
        const res = moment.utc(props.instruction.period.selectionStart);

        if (res.year() < 2000) {
          return moment.utc();
        }

        return res;
      }
    }
  };

  const startDate = getStartDate();

  const miniCalendarApi = useCustomQuery({
    queryFn: async () => window.CoreApi.Show.GetMiniCalendarData(
      startDate.year(),
      startDate.month() + 1,
      getCacheFriendlyInstruction(props.instruction)
    ).then(res => res.data),
    queryKey: ["showsCount", startDate.format("MM/yyyy"), {
      ...props.instruction,
      page: 1,
      perPage: 50
    }],
    onSuccess: () => { setIsInitialLoading(false); },
    enabled: !props.isDoingFirstLoad
  });

  const previousMonth = () => {
    const newMoment = startDate.clone().subtract(1, "month");
    onMonthSelected(newMoment.month(), newMoment.year());
  };

  const nextMonth = () => {
    const newMoment = startDate.clone().add(1, "month");
    onMonthSelected(newMoment.month(), newMoment.year());
  };

  const currentMonth = () => {
    onMonthSelected(startDate.month(), startDate.year());
  };

  const daysOfWeek = firstDayOfWeek() === 1 ? serverReference.abbreviatedDayLetters.slice(1).concat(serverReference.abbreviatedDayLetters[0]) : serverReference.abbreviatedDayLetters;

  // If the show list is still being fetched for the first time OR if the calendar is being loaded for the first time, keep showing the placeholder calendar
  if (isInitialLoading || props.isDoingFirstLoad) {
    return <PlaceholderCalendar />;
  }

  return <div className="s1_panel show-list-calendar-wrapper">
    <div className="flex">
      <PhosphorCaretLeftButton
        {...HorizontalKeyboardNavigationGroup("calendar")}
        onClick={previousMonth}
        layout="in-square"
      />

      <div
        onMouseEnter={() => { setHighlightingMonthNr(startDate.month()); }}
        onMouseLeave={() => { setHighlightingMonthNr(undefined); }}
        onClick={currentMonth}
        className="grow1"
        style={{ textAlign: "center", cursor: "pointer" }}>
        <strong>{serverReference.abbreviatedMonthNames[startDate.month()]} {startDate.year()}</strong>
      </div>
      <PhosphorCaretRightButton
        {...HorizontalKeyboardNavigationGroup("calendar")}
        onClick={nextMonth}
        layout="in-square"
      />
    </div>

    <div className="flex-column gap-mini">
      <div className="dayofweekrow">
        <div />
        {daysOfWeek.map((dayOfWeek, idx) => <div key={idx}>{dayOfWeek}</div>)}
      </div>
      {miniCalendarApi.isLoading &&
        <Loading height={7 * 28} />
      }
      {!miniCalendarApi.isLoading &&
        <>

          {miniCalendarApi.data.data.weeks.map((weekRow) =>
            <WeekRow
              key={[weekRow.week, weekRow.year].join("-")}
              week={weekRow}
              onWeekClicked={onWeekClicked}
              onDayClicked={onDayClicked}
              highlightedMonth={highlightingMonthNr}
            />
          )}
        </>
      }
    </div>
  </div>;
};

export default Calendar;
